import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Space, Typography, message } from "antd";
import SignatureCanvas from "react-signature-canvas";
import { useFetcher } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { SaveReceiptSignatureResult } from "~/types/receiptSignature";
import { RECEIPT_SIGNATURE_FILENAME } from "~/utils/finance/receiptSignature";
import { INTENTS } from "~/utils";

export type ReceiptSignaturePadProps = {
  initialSignatureUrl?: string | null;
};

type SignatureMode = "view" | "edit";

function isSaveFailure(data: unknown): data is { success: false; error: string } {
  return (
    typeof data === "object" &&
    data != null &&
    "success" in data &&
    (data as { success: boolean }).success === false
  );
}

export function ReceiptSignaturePad({
  initialSignatureUrl = null,
}: ReceiptSignaturePadProps) {
  const { t } = useTranslation();
  const fetcher = useFetcher({ key: "receipt-signature" });
  const sigRef = useRef<SignatureCanvas | null>(null);
  const lastHandledKeyRef = useRef<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(
    initialSignatureUrl,
  );
  const [mode, setMode] = useState<SignatureMode>(
    initialSignatureUrl ? "view" : "edit",
  );

  const saving = fetcher.state !== "idle";

  useEffect(() => {
    setSignatureUrl(initialSignatureUrl);
    setMode(initialSignatureUrl ? "view" : "edit");
  }, [initialSignatureUrl]);

  useEffect(() => {
    if (fetcher.state !== "idle" || fetcher.data == null) {
      return;
    }

    const actionKey = `${fetcher.state}:${JSON.stringify(fetcher.data)}`;
    if (lastHandledKeyRef.current === actionKey) {
      return;
    }
    lastHandledKeyRef.current = actionKey;

    if (isSaveFailure(fetcher.data)) {
      message.error(fetcher.data.error ?? t("Receipt_signature_save_failed"));
      return;
    }

    const data = fetcher.data as SaveReceiptSignatureResult;
    if (data.success) {
      setSignatureUrl(data.receiptSignatureUrl);
      setMode("view");
      sigRef.current?.clear();
      message.success(t("Receipt_signature_saved"));
    }
  }, [fetcher.data, fetcher.state, t]);

  const handleClear = () => {
    sigRef.current?.clear();
  };

  const handleCancelEdit = () => {
    sigRef.current?.clear();
    setMode("view");
  };

  const handleSave = () => {
    const canvas = sigRef.current;
    if (!canvas || canvas.isEmpty()) {
      message.warning(t("Receipt_signature_empty"));
      return;
    }

    const dataUrl = canvas.getCanvas().toDataURL("image/png");
    fetch(dataUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], RECEIPT_SIGNATURE_FILENAME, {
          type: "image/png",
        });
        const formData = new FormData();
        formData.append("intent", INTENTS._save_receipt_signature);
        formData.append("signature", file);
        fetcher.submit(formData, {
          method: "POST",
          encType: "multipart/form-data",
        });
      })
      .catch((error) => {
        console.error("Failed to prepare receipt signature:", error);
        message.error(t("Receipt_signature_save_failed"));
      });
  };

  return (
    <Card title={t("Receipt_signature")} style={{ marginTop: 24 }}>
      {mode === "view" && signatureUrl ? (
        <>
          <Typography.Paragraph type="secondary">
            {t("Receipt_signature_current")}
          </Typography.Paragraph>
          <div
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              background: "#fff",
              maxWidth: 520,
              padding: 8,
            }}
          >
            <img
              src={signatureUrl}
              alt={t("Receipt_signature")}
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: 160,
                objectFit: "contain",
              }}
            />
          </div>
          <Space style={{ marginTop: 12 }}>
            <Button type="primary" onClick={() => setMode("edit")}>
              {t("Update_signature")}
            </Button>
          </Space>
        </>
      ) : (
        <>
          <Typography.Paragraph type="secondary">
            {signatureUrl
              ? t("Receipt_signature_update_help")
              : t("Receipt_signature_help")}
          </Typography.Paragraph>
          <div
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              background: "#fff",
              maxWidth: 520,
            }}
          >
            <SignatureCanvas
              ref={sigRef}
              canvasProps={{
                width: 500,
                height: 160,
                style: { width: "100%", height: 160, display: "block" },
              }}
              penColor="#000000"
            />
          </div>
          <Space style={{ marginTop: 12 }}>
            <Button onClick={handleClear} disabled={saving}>
              {t("Clear")}
            </Button>
            {signatureUrl ? (
              <Button onClick={handleCancelEdit} disabled={saving}>
                {t("Cancel")}
              </Button>
            ) : null}
            <Button type="primary" onClick={handleSave} loading={saving}>
              {t("Save_signature")}
            </Button>
          </Space>
        </>
      )}
    </Card>
  );
}
